import React, { useEffect, useState } from 'react';
import type { Toast as ToastType } from '@/contexts/ToastContext';

interface ToastProps {
  toast: ToastType;
  onRemove: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Auto-remove after duration
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onRemove(toast.id), 300);
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.id, onRemove]);

  const typeStyles = {
    success: 'bg-[#388e3c] border-[#4caf50]',
    error: 'bg-[#d32f2f] border-[#f44336]',
    info: 'bg-[#1976d2] border-[#2196f3]',
    warning: 'bg-[#f57c00] border-[#ff9800]',
  };

  const icons = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
    warning: '⚠',
  };

  return (
    <div
      className={`
        ff-border px-4 py-3 mb-2 flex items-center gap-3 min-w-[300px] max-w-md
        ${typeStyles[toast.type]}
        ${isExiting ? 'animate-slide-out' : 'animate-slide-in'}
      `}
    >
      <span className="text-xl font-bold">{icons[toast.type]}</span>
      <p className="text-pixel-sm flex-1">{toast.message}</p>
      <button
        onClick={() => {
          setIsExiting(true);
          setTimeout(() => onRemove(toast.id), 300);
        }}
        className="text-lg leading-none hover:scale-110 transition-transform"
        aria-label="Close"
      >
        &times;
      </button>
    </div>
  );
};

export default Toast;
